import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const getMotionAllowed = () => (
  typeof window !== 'undefined' &&
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

const createPoint = (width, height, index) => ({
  x: ((index * 197) % width) + Math.random() * 18,
  y: ((index * 113) % height) + Math.random() * 18,
  vx: (Math.random() - 0.5) * 0.22,
  vy: (Math.random() - 0.5) * 0.22,
});

const AppWebBackground = () => {
  const canvasRef = useRef(null);
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const context = canvas.getContext('2d');
    const motionAllowed = getMotionAllowed();
    const colors = isLight
      ? {
        glowInner: 'rgba(79, 122, 101, 0.18)',
        glowMid: 'rgba(79, 122, 101, 0.08)',
        glowOuter: 'rgba(79, 122, 101, 0)',
        line: 'rgba(47, 96, 75, ',
        lineAlpha: 0.42,
        point: 'rgba(47, 96, 75, 0.72)',
      }
      : {
        glowInner: 'rgba(148, 191, 166, 0.2)',
        glowMid: 'rgba(148, 191, 166, 0.085)',
        glowOuter: 'rgba(148, 191, 166, 0)',
        line: 'rgba(148, 191, 166, ',
        lineAlpha: 0.34,
        point: 'rgba(209, 230, 219, 0.56)',
      };
    let frameId;
    let points = [];

    const resize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const ratio = window.devicePixelRatio || 1;
      canvas.width = Math.floor(width * ratio);
      canvas.height = Math.floor(height * ratio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      points = Array.from(
        { length: Math.max(46, Math.floor((width * height) / 16000)) },
        (_, index) => createPoint(width, height, index)
      );
    };

    const draw = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      context.clearRect(0, 0, width, height);

      const gradient = context.createRadialGradient(
        width * 0.52,
        height * 0.48,
        0,
        width * 0.52,
        height * 0.48,
        Math.max(width, height) * 0.72
      );
      gradient.addColorStop(0, colors.glowInner);
      gradient.addColorStop(0.55, colors.glowMid);
      gradient.addColorStop(1, colors.glowOuter);
      context.fillStyle = gradient;
      context.fillRect(0, 0, width, height);

      points.forEach((point, index) => {
        if (motionAllowed) {
          point.x += point.vx;
          point.y += point.vy;
          if (point.x < 0 || point.x > width) point.vx *= -1;
          if (point.y < 0 || point.y > height) point.vy *= -1;
        }

        for (let nextIndex = index + 1; nextIndex < points.length; nextIndex += 1) {
          const next = points[nextIndex];
          const distance = Math.hypot(point.x - next.x, point.y - next.y);
          if (distance > 190) continue;

          context.strokeStyle = `${colors.line}${colors.lineAlpha * (1 - distance / 190)})`;
          context.lineWidth = distance < 95 ? 1.2 : 0.85;
          context.beginPath();
          context.moveTo(point.x, point.y);
          context.lineTo(next.x, next.y);
          context.stroke();
        }

        context.fillStyle = colors.point;
        context.beginPath();
        context.arc(point.x, point.y, 1.7, 0, Math.PI * 2);
        context.fill();
      });

      if (motionAllowed) frameId = window.requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, [isLight]);

  return (
    <Box
      aria-hidden="true"
      component="canvas"
      ref={canvasRef}
      sx={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 0,
        pointerEvents: 'none',
        opacity: isLight ? { xs: 0.62, sm: 0.78 } : { xs: 0.66, sm: 0.82 },
        mixBlendMode: isLight ? 'multiply' : 'screen',
      }}
    />
  );
};

export default AppWebBackground;
